"use client";

import { useCallback } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useIdleLogout } from "@/hooks/use-idle-logout";
import { useIsMobile } from "@/hooks/use-mobile";
import { useOnlineStatus } from "@/hooks/use-online-status";
import { trackSessionEnded } from "@/lib/analytics";
import { signOut } from "@/lib/auth";
import { clearAllOfflineData } from "@/lib/offline/db";

const IDLE_MS = 50_000;
const WARNING_MS = 10_000;

export function InactivityLogoutWatcher() {
  const isMobile = useIsMobile();
  const isOnline = useOnlineStatus();
  const enabled = isMobile && isOnline;

  const handleTimeout = useCallback(async () => {
    const sessionStartTime = sessionStorage.getItem("session_start_time");
    const sessionDurationSeconds = sessionStartTime
      ? Math.floor((Date.now() - parseInt(sessionStartTime, 10)) / 1000)
      : 0;
    trackSessionEnded("inactivity_timeout", sessionDurationSeconds);
    sessionStorage.removeItem("session_start_time");

    await clearAllOfflineData();
    await signOut();
    window.location.href = "/login";
  }, []);

  const { isWarning, secondsRemaining, dismissWarning } = useIdleLogout({
    idleMs: IDLE_MS,
    warningMs: WARNING_MS,
    enabled,
    onTimeout: handleTimeout,
  });

  if (!enabled) return null;

  return (
    <AlertDialog open={isWarning}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            You&apos;re about to be signed out
          </AlertDialogTitle>
          <AlertDialogDescription aria-live="polite">
            For security, you&apos;ll be signed out in {secondsRemaining} second
            {secondsRemaining === 1 ? "" : "s"}. Tap below to stay signed in.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={dismissWarning}>
            Stay signed in
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
